import clsx from 'clsx';
import { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../appContext/AppContext';
import { useDebounce } from '../../utils/AppFunction';
import InputSearch from '../InputSearch';
import './style.scss';
import { INode, TreeProps } from './types';
import TreeNode from './TreeNode';

const Tree = (props: TreeProps) => {
	const {
		treeData,
		onSelect,
		heightBody,
		contextMenuItems,
		hasExpandAll = true,
		fetchGetChildren,
		title,
		onNodeExpand,
		className,
		defaultSelectedId = null,
	} = props;
	const { setIsLoading } = useContext(AppContext);
	const [keyword, setKeyword] = useState<string>('');
	const [listNode, setListNode] = useState<any>([]);
	const [expandAll, setExpandAll] = useState<boolean>(hasExpandAll);
	const [selectedNode, setSelectedNode] = useState<string | null>(defaultSelectedId);

	const keywordDebounce = useDebounce(keyword, 300);
	
	useEffect(() => {
		setListNode(assignPathIds(treeData));
	}, [treeData]);

	useEffect(() => {
		setListNode(filterTree(keywordDebounce));
	}, [keywordDebounce]);

	const assignPathIds = (nodes: any, { idKey = 'nodeId', childrenKey = 'subs' } = {}) => {
		function walk(list: any[], pathPrefix = '') {
			return list.map((node, index) => {
				const path = pathPrefix === '' ? `${index}` : `${pathPrefix}-${index}`;
				const newNode = { ...node, [idKey]: path };

				if (node[childrenKey] && Array.isArray(node[childrenKey])) {
					newNode[childrenKey] = walk(node[childrenKey], path);
				}

				return newNode;
			});
		}
		return walk(nodes);
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setKeyword(e.target.value);
	};

	const handleSelectNode = (item: INode | null) => {
		setSelectedNode(item?.nodeId ?? null);
		onSelect?.(item);
	};

	const filterTree = (keyword: string) => {
		if (!keyword) return treeData;
		const matchAndFilter = (node: any) => {
			const hasMatch =
				!node?.subs?.length && node?.title?.toLowerCase().includes(keyword.toLowerCase());

			let filteredChildren = [];
			if (node?.subs?.length) {
				filteredChildren = node.subs.map(matchAndFilter).filter(Boolean);
			}

			if (hasMatch || filteredChildren?.length) {
				return {
					...node,
					subs: filteredChildren?.length ? filteredChildren : undefined,
				};
			}

			return null;
		};

		return treeData?.map(matchAndFilter)?.filter(Boolean);
	};

	const handleSearch = () => {
		const filteredData = filterTree(keyword);
		setListNode(filteredData);
		setExpandAll(true);
	};

	const handleKeyDownSearch = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			handleSearch();
		}
	};

	const handleGetChildren = async (idNode: string) => {
		if (fetchGetChildren) {
			setIsLoading(true);
			try {
				const response = await fetchGetChildren(idNode);
				const updatedListNode = (listNode: any) => {
					return listNode?.map((node: INode) => {
						if (node?.id === idNode) {
							return {
								...node,
								subs: response,
							};
						} else if (node?.subs) {
							return {
								...node,
								subs: updatedListNode(node?.subs),
							};
						}
						return node;
					});
				};
				setListNode(updatedListNode(listNode));
			} catch (error) {
				console.error('Error fetching subs:', error);
			} finally {
				setIsLoading(false);
			}
		}
	};

	return (
		<div className={clsx("overflow-y-auto", className)}>
			<div>
				{title && <div className="fw-bold p-2 bg-gray">Tài liệu bệnh án</div>}
				<div>
					<InputSearch
						handleChange={handleInputChange}
						handleSearch={handleSearch}
						handleKeyDown={handleKeyDownSearch}
						placeholder="Tìm kiếm"
					/>
				</div>
			</div>
			<div className={clsx('spaces overflow-auto', heightBody)}>
				<ul className="list-group">
					{listNode?.map((node: INode, index: number) => (
						<li key={index} className="list-group-item border-0 py-1">
							<TreeNode
								node={node}
								nodes={listNode}
								handleSelectNode={handleSelectNode}
								expandAll={expandAll}
								contextMenuItems={contextMenuItems}
								handleGetChildren={handleGetChildren}
								hasExpandAll={hasExpandAll}
								onNodeExpand={onNodeExpand}
								selectedNode={selectedNode}
							/>
						</li>
					))}
				</ul>
			</div>
		</div>
	);
};

export default Tree;
